import {
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  type LucideIcon,
} from "lucide-react";
import { TONES, type StatusTone } from "../filters/statusColors";

/** File kinds shared by the file gallery and the attachments panel, so a PDF
 *  tile looks the same on every documents tab. */
export type Kind =
  "image" | "pdf" | "word" | "sheet" | "video" | "audio" | "other";

export const extOf = (name: string) =>
  name.split(".").pop()?.toLowerCase() ?? "";

/** What a file is, from its MIME type, the loose type the API stores
 *  ("image", "document"...), and finally its extension. */
export function kindOf(type: string | undefined, name: string): Kind {
  const ext = extOf(name);
  if (
    type?.startsWith("image") ||
    ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext)
  )
    return "image";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (
    type?.includes("wordprocessing") ||
    type === "application/msword" ||
    ["doc", "docx"].includes(ext)
  )
    return "word";
  if (
    type?.includes("spreadsheet") ||
    type === "application/vnd.ms-excel" ||
    ["xls", "xlsx", "csv"].includes(ext)
  )
    return "sheet";
  if (type?.startsWith("video") || type === "video") return "video";
  if (type?.startsWith("audio") || type === "audio") return "audio";
  return "other";
}

/** Each kind's tile: the same soft tones as status pills, so a PDF reads the
 *  same in the gallery as anywhere else, without a loud block of colour. */
export const KIND_STYLE: Record<Kind, { tone: StatusTone; icon: LucideIcon }> =
  {
    image: { tone: TONES.success, icon: ImageIcon },
    pdf: { tone: TONES.danger, icon: FileText },
    word: { tone: TONES.info, icon: FileText },
    sheet: { tone: TONES.success, icon: FileSpreadsheet },
    video: { tone: TONES.escalated, icon: Video },
    audio: { tone: TONES.decision, icon: Music },
    other: { tone: TONES.neutral, icon: FileIcon },
  };
